import {Card, Col, Row, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import {Loading} from "../../components/Loading/Loading"
import {limitOptionsSelect} from "../../helps/optionsSelect"
import {useHistory, useLocation} from "react-router-dom"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {getUnregisterPSLeakDevices, setUnregisterPSLeakDeviceFilter, setUnregisterPSLeakDevices, setWSUnregisterPSLeakDevice} from "../../store/unregisterPsLeakDeviceReducer"
import {FormSelect} from "../../components/Form/FormSelect"
import {FormInput} from "../../components/Form/FormInput"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"

export const UnregisterPSLeakDevices = () =>
{
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const psLeakDevices = useSelector((state: StateType) => state.unregisterPsLeakDeviceReducer.unregisterPsLeakDevices)
    const filter = useSelector((state: StateType) => state.unregisterPsLeakDeviceReducer.filter)
    const ws = useSelector((state: StateType) => state.unregisterPsLeakDeviceReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const columns = useMemo(() =>
    {
        const cols = []

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: "Компания",
            dataIndex: "nameCompany",
            key: "nameCompany",
            align: center,
        })

        cols.push(
            {
                title: "Название",
                dataIndex: "name",
                key: "name",
                align: center,
            },
            {
                title: "UIN",
                dataIndex: "uin",
                key: "uin",
                align: center,
            },
            {
                title: "MAC адрес",
                dataIndex: "macAddress",
                key: "macAddress",
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setUnregisterPSLeakDeviceFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getPSLeakDevicesFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (location?.search)
        {
            dispatch(getUnregisterPSLeakDevices({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() =>
    {
        getPSLeakDevicesFunc()

        return () =>
        {
            dispatch(setUnregisterPSLeakDevices(null))
        }
    }, [dispatch, getPSLeakDevicesFunc])

    useEffect(() =>
    {
        return () =>
        {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() =>
    {
        if (ws)
        {
            getPSLeakDevicesFunc(true)
        }
        dispatch(setWSUnregisterPSLeakDevice(null))
    }, [dispatch, ws, getPSLeakDevicesFunc])

    const data = useMemo(() => psLeakDevices?.data?.map(i =>
    {
        return {
            key: i.id,
            id: i.id,
            uin: i.uin,
            name: i.name,
            macAddress: i.macAddress,
            idCompany: i.idCompany,
            nameCompany: i.nameCompany,
        }
    }), [psLeakDevices])

    return (
        <div className="tabled">
            <Helmet>
                <title>Не зарегистрированное оборудование</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className="table-title">
                                  <div>Не зарегистрированное оборудование</div>
                              </div>
                          }
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                psLeakDevices
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter.limit ? {
                                                 position: psLeakDevices?.data?.length >= 10 ? ["bottomLeft", "topLeft"] : ["bottomLeft"],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: psLeakDevices.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) =>
                                                 {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id, uin, name, idCompany, macAddress}) => ({
                                                 onClick: () => history.push(`/psLeakDeviceForm`, {uin, name, idCompany, macAddress}),
                                                 onMouseDown: (e) =>
                                                 {
                                                     const params = new URLSearchParams({uin: uin ?? "", name: name ?? "", macAddress: macAddress ?? ""}).toString()
                                                     e.button === 1 && window.open(`/psLeakDeviceForm?${params}`, "_blank", "noopener,noreferrer")
                                                 },
                                             })}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
